import { NoSstFound } from '@features/ssts/components/NoSstFound';
import { SelectDateRangeFilter } from '@features/ssts/components/SelectDateRangeFilter';
import { SstForumFilters } from '@features/ssts/components/SstForumFilters';
import { SstSortSelect } from '@features/ssts/components/SstSortSelect';
import { SstTable } from '@features/ssts/components/SstTable';
import { useSstUrlState } from '@features/ssts/hooks/useSstUrlState';
import { TableContainer } from '@features/ui-atoms/components/TableV2/Table';
import { FilterGroup } from '@features/ui-filters/components/types';
import Header, { HeaderLeft, HeaderTitle } from '@features/ui-layout/components/Header';
import { LayoutLoggedin } from '@features/ui-layout/templates/LayoutLoggedin';
import Typography from '@mui/joy/Typography';
import { sstQueries } from '@root/src/entities/Sst/queries';
import { SstFront } from '@root/src/entities/Sst/type';
import getRoute from '@root/src/services/routeList';
import { useQuery } from '@tanstack/react-query';
import { NextPageWithLayout } from '@utils/types/NextPageWithLayout';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const SSTForumPage: NextPageWithLayout = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const {
    page,
    filters,
    sort,
    handleSortChange,
    apiFilters,
    handleFilterChange,
    handlePageChange,
    hasActiveFilters,
    resetSearch,
  } = useSstUrlState();

  const { data: { items: ssts = [], pages = 0, total } = {}, isLoading } = useQuery(
    sstQueries.list({ page, filters: apiFilters as FilterGroup, sort }),
  );

  const handleRowClick = (sst: SstFront) => {
    void router.push(getRoute.sst(sst.id));
  };

  return (
    <LayoutLoggedin>
      <Header>
        <HeaderLeft>
          <HeaderTitle title={t('page.sstForum.title')} />
        </HeaderLeft>
      </Header>
      <div style={{ paddingTop: '84px', paddingLeft: '32px', paddingRight: '32px' }}>
        <TableContainer>
          <SstForumFilters
            filters={filters}
            hasActiveFilters={hasActiveFilters}
            handleFilterChange={handleFilterChange}
            resetFilters={resetSearch}
          />
          {ssts.length > 0 || isLoading ? (
            <div style={{ width: '100%', maxWidth: '1240px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'end',
                  paddingTop: '32px',
                  paddingBottom: '12px',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                  <Typography level="section-title">{t('ssts')}</Typography>
                  {!isLoading && <Typography level="legend-medium">&nbsp;({total})</Typography>}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'end' }}>
                  <div style={{ width: 280 }}>
                    <SstSortSelect sort={sort} onSortChange={handleSortChange} />
                  </div>
                  <SelectDateRangeFilter filters={filters} handleFilterChange={handleFilterChange} />
                </div>
              </div>
              <SstTable
                data={ssts}
                currentPage={page}
                pageCount={pages}
                onPageChange={handlePageChange}
                isLoading={isLoading}
                onRowClick={handleRowClick}
              />
            </div>
          ) : (
            <NoSstFound onResetFilters={resetSearch} />
          )}
        </TableContainer>
      </div>
    </LayoutLoggedin>
  );
};

SSTForumPage.getLayout = page => page;

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? 'en', ['common'])),
  },
});

export default SSTForumPage;
